import React, { useState, useEffect } from "react";
import { manuscriptTypes, researchAreas } from "../../constants/types.jsx";

/**
 * PaperBriefDescriptionEdit Component
 * 
 * Enhanced brief description component for admin paper editing
 * that includes paper status management.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data
 * @param {Function} props.handleChange - Change handler
 * @param {Array} props.conferences - Available conferences
 */
const PaperBriefDescriptionEdit = ({ formData, handleChange, conferences }) => {
  // State to track selected categories for each research area
  const [selectedCategories, setSelectedCategories] = useState({
    researchArea1: "",
    researchArea2: "",
    researchArea3: ""
  });

  // Helper function to find category for a given topic
  const findCategoryForTopic = (topic) => {
    if (!topic) return "";
    for (const [category, topics] of Object.entries(researchAreas)) {
      if (topics.includes(topic)) {
        return category;
      }
    }
    return "";
  };

  // Initialize selected categories based on existing form data
  useEffect(() => {
    setSelectedCategories({
      researchArea1: findCategoryForTopic(formData.researchArea1),
      researchArea2: findCategoryForTopic(formData.researchArea2),
      researchArea3: findCategoryForTopic(formData.researchArea3)
    });
  }, [formData.researchArea1, formData.researchArea2, formData.researchArea3]);

  /**
   * Creates category options for the first level of selection
   * @returns {Array} Array of option elements with research area categories
   */
  const createCategoryOptions = () => {
    return Object.keys(researchAreas).map((category, index) => (
      <option key={`category-${index}`} value={category}>
        {category}
      </option>
    ));
  };

  /**
   * Creates topic options for the second level of selection based on selected category
   * @param {string} selectedCategory - The selected research area category
   * @returns {Array} Array of option elements with topics for the selected category
   */
  const createTopicOptions = (selectedCategory) => {
    if (!selectedCategory || !researchAreas[selectedCategory]) {
      return [];
    }
    
    return researchAreas[selectedCategory].map((topic, index) => (
      <option key={`topic-${index}`} value={topic}>
        {topic}
      </option>
    ));
  };

  /**
   * Handle category selection change
   * @param {Event} event - The select change event
   * @param {string} fieldName - The field name (researchArea1, researchArea2, etc.)
   */
  const handleCategoryChange = (event, fieldName) => {
    const selectedCategory = event.target.value;
    
    // Update the selected category
    setSelectedCategories(prev => ({
      ...prev,
      [fieldName]: selectedCategory
    }));
    
    // Clear the corresponding topic selection
    handleChange({
      target: {
        name: fieldName,
        value: ""
      }
    });
  };

  /**
   * Handle topic selection change
   * @param {Event} event - The select change event
   */
  const handleTopicChange = (event) => {
    handleChange(event);
  };

  const statusOptions = [
    { value: 'under_review', label: 'Under Review' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'revision_required', label: 'Revision Required' },
    { value: 'incomplete', label: 'Update-Revise' },
    { value: 'withdrawn', label: 'Withdrawn' }
  ];

  return (
    <div>
      <h4 className="mb-4">Paper Information</h4>

      <div className="row">
        {/* Manuscript Type */}
        <div className="col-md-6 mb-3">
          <label htmlFor="manuscriptType" className="form-label">
            Manuscript Type *
          </label>
          <select
            className="form-select"
            id="manuscriptType"
            name="manuscriptType"
            value={formData.manuscriptType}
            onChange={handleChange}
            required
          >
            <option value="">Select manuscript type</option>
            {manuscriptTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
          </select>
        </div>

        {/* Paper Status - Admin Only */}
        <div className="col-md-6 mb-3">
          <label htmlFor="status" className="form-label">
            <i className="bi bi-shield-check me-2"></i>
            Paper Status *
          </label>
          <select
            className="form-select"
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
          >
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
          <div className="form-text">
            <i className="bi bi-info-circle me-1"></i>
            Admin only: Change the review status of this paper
          </div>
        </div>
      </div>

      {/* Conference Selection */}
      {/* <div className="mb-3">
        <label htmlFor="conferenceId" className="form-label">
          Conference *
        </label>
        <select
          className="form-select"
          id="conferenceId"
          name="conferenceId"
          value={formData.conferenceId}
          onChange={handleChange}
          required
        >
          <option value="">Select conference</option>
          {conferences?.map(conference => (
            <option key={conference._id} value={conference._id}>
              {conference.title} - {conference.location} ({new Date(conference.startDate).getFullYear()})
            </option>
          ))}
        </select>
      </div> */}

      {/* Manuscript Title */}
      <div className="mb-3">
        <label htmlFor="manuscriptTitle" className="form-label">
          Manuscript Title *
        </label>
        <input
          type="text"
          className="form-control"
          id="manuscriptTitle"
          name="manuscriptTitle"
          value={formData.manuscriptTitle}
          onChange={handleChange}
          placeholder="Enter the title of your paper"
          required
        />
      </div>

      {/* Abstract */}
      <div className="mb-3">
        <label htmlFor="manuscriptAbstract" className="form-label">
          Abstract *
        </label>
        <textarea
          className="form-control"
          id="manuscriptAbstract"
          name="manuscriptAbstract"
          rows="6"
          value={formData.manuscriptAbstract}
          onChange={handleChange}
          placeholder="Enter the abstract of your paper (maximum 300 words)"
          required
        ></textarea>
        <div className="form-text">
          Current length: {formData.manuscriptAbstract?.length || 0} characters
        </div>
      </div>



      {/* Research Areas Classification */}
      <h5 className="mb-3">Area &amp; Topic of Submitted Manuscript</h5>

      <div className="row">
        {/* Research Area 1 */}
        <div className="col-md-4 mb-3">
          <label htmlFor="researchArea1Category" className="form-label">
            Primary Research Area - Category *
          </label>
          <select
            className="form-select"
            id="researchArea1Category"
            name="researchArea1Category"
            value={selectedCategories.researchArea1 || ""}
            onChange={(e) => handleCategoryChange(e, "researchArea1")}
            required
          >
            <option value="">Select category...</option>
            {createCategoryOptions()}
          </select>
          
          {selectedCategories.researchArea1 && (
            <div className="mt-2">
              <label htmlFor="researchArea1" className="form-label">
                Primary Research Area - Topic *
              </label>
              <select
                className="form-select"
                id="researchArea1"
                name="researchArea1"
                value={formData.researchArea1 || ""}
                onChange={handleTopicChange}
                required
              >
                <option value="">Select topic...</option>
                {createTopicOptions(selectedCategories.researchArea1)}
              </select>
            </div>
          )}
        </div>

        {/* Research Area 2 */}
        <div className="col-md-4 mb-3">
          <label htmlFor="researchArea2Category" className="form-label">
            Secondary Research Area - Category *
          </label>
          <select
            className="form-select"
            id="researchArea2Category"
            name="researchArea2Category"
            value={selectedCategories.researchArea2 || ""}
            onChange={(e) => handleCategoryChange(e, "researchArea2")}
            required
          >
            <option value="">Select category...</option>
            {createCategoryOptions()}
          </select>
          
          {selectedCategories.researchArea2 && (
            <div className="mt-2">
              <label htmlFor="researchArea2" className="form-label">
                Secondary Research Area - Topic *
              </label>
              <select
                className="form-select"
                id="researchArea2"
                name="researchArea2"
                value={formData.researchArea2 || ""}
                onChange={handleTopicChange}
                required
              >
                <option value="">Select topic...</option>
                {createTopicOptions(selectedCategories.researchArea2)}
              </select>
            </div>
          )}
        </div>

        {/* Research Area 3 */}
        <div className="col-md-4 mb-3">
          <label htmlFor="researchArea3Category" className="form-label">
            Tertiary Research Area - Category *
          </label>
          <select
            className="form-select"
            id="researchArea3Category"
            name="researchArea3Category"
            value={selectedCategories.researchArea3 || ""}
            onChange={(e) => handleCategoryChange(e, "researchArea3")}
            required
          >
            <option value="">Select category...</option>
            {createCategoryOptions()}
          </select>
          
          {selectedCategories.researchArea3 && (
            <div className="mt-2">
              <label htmlFor="researchArea3" className="form-label">
                Tertiary Research Area - Topic *
              </label>
              <select
                className="form-select"
                id="researchArea3"
                name="researchArea3"
                value={formData.researchArea3 || ""}
                onChange={handleTopicChange}
                required
              >
                <option value="">Select topic...</option>
                {createTopicOptions(selectedCategories.researchArea3)}
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="alert alert-info mt-3">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Admin Edit Mode:</strong> You can modify all paper details including the review status. 
        Changes will be immediately visible to the submitting author.
      </div>
    </div>
  );
};

export default PaperBriefDescriptionEdit;
